import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { formatEventDateLong, formatTimeShort } from 'utils/dateTime';
import useEventBookingSession from 'features/Events/hooks/useEventBookingSession';
import { useGlobalDateLocale } from 'context/DateLocaleContext';
import { SkeletonLineSlim } from 'components/theme/Skeleton/Skeleton';
import useWaitList from 'api/waitlist/useWaitList';
import getUpdatedSelectedDate from 'utils/data/getUpdatedSelectedData';
import getFirstAvailableDateFromSessions from 'utils/data/firstAvailableDate/getFirstAvailableDateFromSessions';

interface TicketHeaderProps {
  selectedTime: string | null;
}

const TicketHeader = ({ selectedTime }: TicketHeaderProps) => {
  const { locale } = useGlobalDateLocale();
  const { isLoading, listing, date, daySessions } = useEventBookingSession();
  const { data: waitlist } = useWaitList(listing?.waitlistId, listing?.brandId);
  const firstAvailableScheduledDate =
    getFirstAvailableDateFromSessions(daySessions)?.startDateTime?.split(
      'T',
    )[0];
  const selectedDate = getUpdatedSelectedDate(
    date,
    firstAvailableScheduledDate,
    waitlist,
  );
  const { t: translate } = useTranslation();

  if (isLoading || !listing || !selectedDate) {
    return (
      <Wrapper>
        <TitleSkeleton />
        <DateTimeSkeleton />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <Title className="page-heading">{listing.title}</Title>
      <DateTime>
        {formatEventDateLong(selectedDate, locale)}{' '}
        {selectedTime
          ? `${translate('at')} ${formatTimeShort(selectedTime, locale)}`
          : ''}
      </DateTime>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  @media (max-width: 768px) {
    display: none;
  }
`;

const Title = styled.h1`
  margin-top: 0px;
  color: var(--way-colors-primaryTextColor);
  margin-bottom: 6px;
  font-size: 28px;
`;

const DateTime = styled.span`
  color: var(--way-colors-contrastColorShades-60);
`;

const TitleSkeleton = styled(SkeletonLineSlim)`
  width: 50%;
  height: 37px;
  margin-bottom: 6px;
`;

const DateTimeSkeleton = styled(SkeletonLineSlim)`
  width: 33%;
  height: 18px;
  margin-bottom: 10px;
`;

export default TicketHeader;
